import L from 'leaflet'
import { useEffect, useRef } from 'react'
import ReactDOMServer from 'react-dom/server'

import { PlanningShiftGridRow } from '@/entities/gridTables/model/planning/PlanningShiftGridRow'
import { PlanningShiftTable } from '@/entities/gridTables/model/planning/PlanningShiftTable'
import { RunsheetShiftGridRow } from '@/entities/gridTables/model/runsheet/RunsheetShiftGridRow'
import { RunsheetShiftTable } from '@/entities/gridTables/model/runsheet/RunsheetShiftTable'
import { StopTypeEnum } from '@/entities/schedules'
import { selectBusinessModel } from '@/entities/user'
import { LocationMarkerIcon } from '@/shared/components/MapIcons/LocationMarkerIcon'
import { useAppSelector } from '@/shared/store'

export type DrawShiftsArgs = {
  map: L.Map | null
  shiftTables: PlanningShiftTable[] | RunsheetShiftTable[]
}

/**
 * The following hook is used to draw the depots on the map widget.
 * 
 * @param map the Leaflet map object
 * @param depots the depots to plot on the map
 * @returns void
 */
export const useDrawShifts = ({ map, shiftTables }: DrawShiftsArgs) => {
  const orderMarkersRef = useRef<L.LayerGroup | null>(null)
  const businessModel = useAppSelector(selectBusinessModel)

  const createLocationIcon = (color: string, sequence: number, orderStatusType?: string) => {
    const svgString = ReactDOMServer.renderToStaticMarkup(
      <LocationMarkerIcon
        fillColor={color ?? '#000'}
        content={sequence}
        baseOpacity={orderStatusType === 'Completed' ? 0.65 : 1}
        className="depot-icon-svg"
      />,
    )

    return L.divIcon({
      html: svgString,
      className: '',
      iconSize: [30, 40],
      iconAnchor: [15, 40],
    })
  }

  const createLocationTooltip = (
    stopsWithSameLocation: (PlanningShiftGridRow | RunsheetShiftGridRow)[],
    scheduleName: string,
    shiftName: string,
  ) => {
    let tooltip = ''

    if (stopsWithSameLocation.length === 0) {
      return tooltip
    }

    // Add the shift and stop information to the tooltip
    const firstStop = stopsWithSameLocation[0]

    tooltip += `       
                <p style='margin: 2px 0'><strong>Assign to:</strong> ${scheduleName} > ${shiftName}</p>
                <p style='margin: 2px 0'><strong>ETA:</strong> ${firstStop?.etaTime}</p>
                ${stopsWithSameLocation.length > 1 ? `<p style='margin: 2px 0'><strong>Number of stops:</strong> ${stopsWithSameLocation.length}</p>` : ''}
                <hr/>`

    // Iterate through the orders and create a tooltip for each order
    for (let i = 0; i < stopsWithSameLocation.length; i++) {
      const stop = stopsWithSameLocation[i]

      tooltip += `<p style='margin: 2px 0'><strong>Sequence No.:</strong> ${stop.sequence}</p>
              <p style='margin: 2px 0'><strong>Order No.:</strong> ${stop.orderNumber}</p>
              ${stop.statusType && `<p style='margin: 2px 0'><strong>Order Status:</strong> ${stop.statusType}</p>`}
              <p style='margin: 2px 0'><strong>Stop Type:</strong> ${stop.displayStopType}</p>
              <p style='margin: 2px 0'><strong>Stop Address:</strong> ${stop.customerDisplayAddress}</p>`

      if (i < stopsWithSameLocation.length - 1) {
        tooltip += '<hr/>'
      }
    }

    return tooltip
  }

  // Plot orders on the map
  useEffect(() => {
    if (!map || shiftTables?.length === 0) return

    if (orderMarkersRef.current) {
      orderMarkersRef.current.clearLayers()
    } else {
      orderMarkersRef.current = L.layerGroup().addTo(map)
    }


    // We render the orders based on the shift tables
    shiftTables.forEach((shiftTable) => {
      const shiftTableRows = shiftTable?.rows

      if (!shiftTableRows) return

      // Pre-compute the stops that are in the same location
      const coordinatesToRowsLookup = new Map<string, (PlanningShiftGridRow | RunsheetShiftGridRow)[]>()

      for (let i = 0; i < shiftTableRows.length; i++) {
        const row = shiftTableRows[i]

        if (row.stopType !== StopTypeEnum.Order || !row.isJob) continue

        const key = `${row.customerLatitude},${row.customerLongitude}`

        if (!coordinatesToRowsLookup.has(key)) {
          coordinatesToRowsLookup.set(key, [])
        }

        coordinatesToRowsLookup.get(key)!.push(row)
      }

      // Iterate through the rows and plot the markers
      for (let i = 0; i < shiftTableRows.length; i++) {
        const row = shiftTableRows[i]

        // Only plot marker if the stop is an order
        if (row.stopType === StopTypeEnum.Order) {
          if (!row.customerLatitude || !row.customerLongitude) continue

          const key = `${row.customerLatitude},${row.customerLongitude}`
          const stopsWithSameLocation = coordinatesToRowsLookup.get(key) ?? []

          if (stopsWithSameLocation.length === 0) {
            continue
          }

          const orderMarkerIcon = createLocationIcon(row.uiColor, row.sequence, row.statusType)
          const marker = L.marker([row.customerLatitude, row.customerLongitude], { icon: orderMarkerIcon })

          // Add hover opacity javascript listener
          marker.on('mouseover', (e) => {
            const markerElement = e.target.getElement()

            if (markerElement) {
              const svgElement = markerElement.querySelector('svg')

              if (svgElement) {
                svgElement.style.opacity = '1'
              }
            }
          })
          
          marker.on('mouseout', (e) => {
            const markerElement = e.target.getElement()

            if (markerElement) {
              const svgElement = markerElement.querySelector('svg')

              if (svgElement) {
                svgElement.style.opacity = ['Completed', 'Completed by Supervisor'].includes(row.statusType) ? '0.65' : '1'
              }
            }
          })

          // Add Tooltip
          const tooltip = createLocationTooltip(stopsWithSameLocation, shiftTable.scheduleName, shiftTable.shiftName)

          marker.bindTooltip(tooltip,
            {
              permanent: false,
              direction: 'right',
              offset: L.point(15, -25),
            },
          )
          orderMarkersRef.current!.addLayer(marker)
        }
      }
    })

    return () => {
      // Need to clear the markers when the component unmounts
      //   so it can be re-added to the map when the component mounts again
      if (orderMarkersRef.current) {
        orderMarkersRef.current.clearLayers()
        orderMarkersRef.current = null
      }
    }
  }, [map, businessModel, shiftTables])
}